import * as React from "react";
import { MobileOverviewScreenStyle } from "./mobile-overview-screen-style";
import UserControl from "../../../ui/smart/user-control/user-control";
import { RootState, TileDescriptionsState } from "../../../../store/types";
import { connect } from "react-redux";
import { UserDataAndState } from "common/Database";
import { toTileDescription, BuilderTileDescription } from "../orgs-lib";
import * as actions from "../../../../actions/actions";
import { Icon } from "common/render/icon";
import { AppManifest } from "common/components/SerializedApp";
import { sampleAppIds } from "../../../../samples/sample-app-ids";
import { sampleAppManifests } from "../../../../samples/sample-app-manifests";
import { trackEvent } from "../../../../analytics";
import { MappedThunk } from "../../../../actions/action-thunk";
import { exceptionToError } from "common/Support";

interface Props {
    readonly userData: UserDataAndState;
    readonly tiles: TileDescriptionsState;
    readonly loadTileDescriptionFromFirebase: MappedThunk<typeof actions.loadTileDescriptionFromFirebase>;
}
interface State {}

class MobileOverviewScreen extends React.PureComponent<Props, State> {
    public state: State = {};

    private createAppTile = (tile: BuilderTileDescription) => {
        return (
            <a className="moss-app" key={tile.appID} href={tile.url} target="_blank" rel="noopener noreferrer">
                <Icon size={64} clipCorners={true} color={tile.primaryAccentColor} image={tile.iconImage} />
                <div className="moss-app-title">{tile.title}</div>
            </a>
        );
    };

    public render(): React.ReactNode {
        const { userData, tiles, loadTileDescriptionFromFirebase } = this.props;

        const samples = sampleAppIds.map((id, i) => <MobileSampleAppTile key={i} appId={id} />);

        const MobileLabeledAppGrid = (props: { label: string; apps: React.ReactNode[] }) =>
            props.apps.length === 0 ? null : (
                <>
                    <h2 className="moss-section-title">{props.label}</h2>
                    {props.apps}
                </>
            );

        let appsNode: React.ReactNode[] = [];
        if (userData !== null && userData !== "loading") {
            appsNode = userData.appIDs
                .map(a => toTileDescription(a, userData.id, tiles, loadTileDescriptionFromFirebase))
                .map(this.createAppTile);
        }

        return (
            <MobileOverviewScreenStyle>
                <div className="moss-scroll-region">
                    <div className="moss-mobile-container">
                        <MobileLabeledAppGrid label="My Apps" apps={appsNode} />
                        <MobileLabeledAppGrid label="Sample Apps" apps={samples} />
                        <div className="moss-mobile-warning">Open Glide on a large screen to build apps.</div>
                    </div>
                </div>

                <div className="moss-footer">
                    <UserControl />
                </div>
            </MobileOverviewScreenStyle>
        );
    }
}

const mapState = (state: RootState) => ({
    userData: state.userData,
    tiles: state.tiles,
});

const mapDispatch = {
    loadTileDescriptionFromFirebase: actions.loadTileDescriptionFromFirebase,
};

export default connect(mapState, mapDispatch)(MobileOverviewScreen);

const manifestCache: { [id: string]: AppManifest } = {};

interface SampleState {
    manifest?: AppManifest;
    error?: Error;
}

interface SampleProps {
    appId: string;
}

class MobileSampleAppTile extends React.Component<SampleProps, SampleState> {
    public state: SampleState = {
        manifest: undefined,
        error: undefined,
    };

    public static readonly Skeleton = (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a className="moss-app" id={""} href={"#"} target="_blank" rel="noopener noreferrer">
            <Icon size={64} clipCorners={true} color="#eee" />
            <div className="moss-app-title">&nbsp;</div>
        </a>
    );

    public async componentDidMount() {
        const { appId } = this.props;

        let manifest = manifestCache[appId];
        if (manifest === undefined) {
            // We try to read a bundled manifest before fetching an up-to-date one
            this.setState({ manifest: sampleAppManifests[appId] });

            try {
                manifest = await fetch(`https://go.glideapps.com/getManifest/manifest.json?id=${appId}`).then(r =>
                    r.json()
                );
                manifestCache[appId] = manifest;
            } catch (error: unknown) {
                this.setState({ error: exceptionToError(error) });
            }
        }

        this.setState({ manifest });
    }

    public render(): React.ReactNode {
        const { appId } = this.props;
        const { manifest, error } = this.state;
        if (error !== undefined) {
            return null;
        }
        if (manifest === undefined) {
            return MobileSampleAppTile.Skeleton;
        }
        return (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a
                className="moss-app"
                id={appId}
                onClick={() => {
                    window.open(manifest.start_url, "_blank");
                    trackEvent("mobile open sample app", { app_id: appId });
                }}>
                <Icon
                    size={64}
                    clipCorners={true}
                    color={manifest.background_color}
                    image={{ url: manifest.icons[3].src }}
                />
                <div className="moss-app-title">{manifest.name}</div>
            </a>
        );
    }
}
